<script setup lang="ts">
import { computed, ref } from 'vue';
import { useField, useForm } from 'vee-validate';
import { v4 as uuid } from 'uuid';

import { ContactPhone, QuestionPhone, VisualFeedbackWrapperTypes, VisualFeedbackWrapper, QuestionYesNo } from '@hfc/ui-core';
import { PHONE_TYPES } from '@hfc/common/src/types/PhoneTypes';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';

import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';

import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionUserInfo from '@/application/shared/infrastructure/components/questions/QuestionUserInfo.vue';
import { DentalAndVisionApplicant } from '@/application/shared/domain/DentalAndVisionApplicant';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { type Applicant } from '@/application/shared/domain/Applicant';
import QuestionAddress from '@/application/shared/infrastructure/components/questions/QuestionAddress.vue';
import AddressValidationModal from '@/application/shared/infrastructure/components/AddressValidationModal.vue';
import { Address } from '@/application/shared/domain/Address';
import { AddressSuggestion } from '@/application/shared/domain/AddressValidationResponse';
import { usePhysicalAddressValidation, AddressValidationStatus } from '@/application/shared/infrastructure/composition/usePhysicalAddressValidation';

const props = withDefaults(defineProps<{
  applicationType: AppTypeDentalOrVision;
  allowQuoteEdit?: boolean;
  obfuscateSsn?: boolean;
}>(), {
  allowQuoteEdit: true,
  obfuscateSsn: true
});

interface emitInterface {
  (event: 'setLoading', value: boolean): void
  (event: 'nextSection'): void
}

const emits = defineEmits<emitInterface>();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const {
  dentalAndVisionApplicationStore,
  primaryApplicant,
  guardian,
  application
} = useDentalAndVisionApplication(props.applicationType);

const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const sectionRef = ref<InstanceType<typeof QuestionUserInfo> | null>(null);
const addressRef = ref<InstanceType<typeof QuestionAddress> | null>(null);
const addressFieldKey = ref(uuid());
const castedApplicants = application.value.applicants as Array<Applicant>;

const {
  status: addressValidationStatus,
  setStatus: setAddressValidationStatus,
  suggestions,
  validate: validatePhysicalAddress,
} = usePhysicalAddressValidation({
  validationFn: dentalAndVisionApplicationStore.validateAddress,
  onStatusChange: (status: AddressValidationStatus) => {
    emits('setLoading', status === 'requested');

    if (status === 'valid') {
      emits('nextSection');
    }
  }
});

const showAddressValidationModal = computed(() => addressValidationStatus.value === 'pending');

const {
  value: sameAddressAsPrimaryApplicant,
  errorMessage: sameAddressAsPrimaryApplicantError,
} = useField('sameAsPrimary', 'yes_no_required', { initialValue: guardian.value.is_address_same_as_main });

const sameAsPrimaryApplicantChanged = (value = true): void => {
  const address = value ? primaryApplicant.value.main_address : dentalAndVisionApplicationStore.generateCustomAddress();
  const index = guardian.value.main_index;

  dentalAndVisionApplicationStore.updateApplicant({ main_address: address, is_address_same_as_main: value }, props.applicationType, index);
};

const setAddressValue = async (value: Partial<Address>): Promise<void> => {
  const address = { ...guardian.value.main_address, ...value, type: ADDRESS_TYPE.HOME } as Address;

  dentalAndVisionApplicationStore.updateApplicant({ main_address: address }, props.applicationType, guardian.value.main_index);
};

const setUserInfoValue = (value: Partial<DentalAndVisionApplicant>): void => {
  dentalAndVisionApplicationStore.updateApplicant(value, props.applicationType, guardian.value.main_index);
};

const setPhoneValue = (value: ContactPhone): void => {
  dentalAndVisionApplicationStore.updateApplicant({ phone: value }, props.applicationType, guardian.value.main_index);
};

const handleAddressConfirmation = async (addressValue: AddressSuggestion | Address): Promise<void> => {
  await setAddressValue(addressValue);
  addressFieldKey.value = uuid();

  sectionSubmit();
};

const confirmAddress = async (address?: AddressSuggestion): Promise<void> => {
  const addressValue = address || guardian.value.main_address;

  if (!addressValue) {
    return;
  }

  await handleAddressConfirmation(addressValue);
};

const validateFormFields = async (): Promise<boolean> => {
  const addressValid = sameAddressAsPrimaryApplicant.value ? true : await addressRef.value?.handleSubmit();
  const localValid = await runValidations();
  const userInfoValid = await sectionRef.value?.handleSubmit();
  const phoneValid = await phoneRef.value?.handleSubmit();

  return Boolean(addressValid && localValid && phoneValid && userInfoValid);
};

const sectionSubmit = async (): Promise<boolean> => {
  const isFormValid = await validateFormFields();
  if (!isFormValid) {
    return false;
  }

  if (sameAddressAsPrimaryApplicant.value) {
    return true;
  }

  if (!guardian.value.main_address) {
    return false;
  }

  await validatePhysicalAddress(guardian.value.main_address);

  return addressValidationStatus.value === 'valid';
};

defineExpose({
  sectionSubmit
});
</script>

<template>
  <BaseSection title="Guardian">
    <AddressValidationModal v-if="showAddressValidationModal" :suggestions="suggestions" :strict-address-validation="true" @confirm="confirmAddress" @close="setAddressValidationStatus('invalid')" :address-type="ADDRESS_TYPE.HOME"/>
    <QuestionUserInfo :value="guardian" ref="sectionRef" @update:value="setUserInfoValue" :auto-focus="true" :hide-suffix="true" :exclude-email-confirmation="true" :obfuscate-ssn="obfuscateSsn" :all-applicants="castedApplicants" />
    <QuestionPhone :value="guardian.phone" :default-phone-type="PHONE_TYPES.MOBILE" @update:value="setPhoneValue" ref="phoneRef" />
    <div class="mb-6 mt-2">
      <QuestionYesNo question-text="Is your address the same as the primary applicant?" id="sameAsPrimary" @update:modelValue="sameAsPrimaryApplicantChanged" name="sameAsPrimary" v-model="sameAddressAsPrimaryApplicant" />
      <span v-if="sameAddressAsPrimaryApplicantError" class="error">{{ sameAddressAsPrimaryApplicantError }}</span>
    </div>
    <VisualFeedbackWrapper v-if="!sameAddressAsPrimaryApplicant" :show-box="addressValidationStatus === 'invalid'" :severity="VisualFeedbackWrapperTypes.ERROR" title="Please enter a valid US home address">
      <QuestionAddress :key="addressFieldKey" :value="guardian.main_address" @update:value="setAddressValue" ref="addressRef" :id="`${guardian.main_address?.type}-${guardian.main_index}`" :application-type="applicationType" :validate-coverage="false" address-type="Home" />
    </VisualFeedbackWrapper>
  </BaseSection>
</template>
